import { type ChangeEvent, useCallback, useEffect } from "react";
import { Form } from "react-bootstrap";
import { XLg } from "react-bootstrap-icons";
import { type CollectionItem, DocumentSubType } from "@/briefpoint-client";
import Button from "@/components/Button";
import PleadingField from "@/components/ConfirmInfo/Components/PleadingField";
import styles from "@/components/ConfirmInfo/ReviewPage.module.scss";
import type { DocumentTypeSelection } from "@/components/ConfirmInfo/SourceDocumentReview";
import {
  pleadCollectionAdd,
  pleadCollectionChange,
  pleadCollectionRemove,
} from "@/components/ConfirmInfo/utils";
import type { CaseResponse } from "@/lib/types";

function ReviewPageFourPd({
  _case,
  allegations,
  selectedRequestTypes,
  setNumber,
  setAllegations,
  setSetNumber,
  setSelectedRequestTypes,
}: {
  _case: CaseResponse | undefined;
  allegations: Array<CollectionItem> | null;
  selectedRequestTypes: DocumentTypeSelection[];
  setNumber: number;
  startingNumber: number;
  setSetNumber: React.Dispatch<React.SetStateAction<number>>;
  setStartingNumber: React.Dispatch<React.SetStateAction<number>>;
  setAllegations: React.Dispatch<
    React.SetStateAction<Array<CollectionItem> | null>
  >;
  setSelectedRequestTypes: React.Dispatch<
    React.SetStateAction<DocumentTypeSelection[]>
  >;
}) {
  // Replace the default extracted values from the document if existing case details are available
  const setExistingCaseDetails = useCallback(async () => {
    if (!_case) return;

    const { allegations } = _case;

    if (allegations && allegations.length > 0) setAllegations(allegations);
  }, [_case, setAllegations]);

  const handleRequestTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    if (checked) {
      setSelectedRequestTypes((selected) => [
        ...selected,
        { subtype: Number(value) },
      ]);
    } else {
      setSelectedRequestTypes((selected) =>
        selected.filter((item) => item.subtype !== Number(value)),
      );
    }
  };

  const handleRequestNumberChange = (
    e: ChangeEvent<HTMLInputElement>,
    subType: DocumentSubType,
  ) => {
    // Default to a number value of 1
    const numberValue = Number.parseInt(e.target.value, 10) || 1;
    setSelectedRequestTypes((selected) => {
      const existingIndex = selected.findIndex(
        (item) => item.subtype === subType,
      );

      if (existingIndex >= 0) {
        const updatedItems = [...selected];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          number: numberValue,
        };
        return updatedItems;
      }
      return selected;
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore setExistingCaseDetails
  useEffect(() => {
    setExistingCaseDetails();
  }, []);

  return (
    <div>
      <div className={styles.pendingSection}>
        <div className={styles.pendingSectionHeader}>
          <p>Allegations</p>
        </div>
        <div className={styles.pendingSectionFields}>
          {allegations !== null && allegations.length > 0 ? (
            <>
              {allegations.map((item, index) => (
                <div className={styles.allegationField} key={index}>
                  <Form.Group
                    key={`allegation-${item.number}`}
                    controlId={`formText${item.number}`}
                  >
                    <PleadingField
                      label={`Allegation ${item.number}`}
                      isTextArea
                      pleadingCollectionIndex={index}
                      pleadingCollection={allegations}
                      setCollection={setAllegations}
                      mappedFieldHandler={pleadCollectionChange}
                      pleadingValue={item.text}
                    />
                    <button
                      type="button"
                      className={styles.pendingRemove}
                      onClick={() =>
                        pleadCollectionRemove(
                          index,
                          allegations,
                          setAllegations,
                        )
                      }
                    >
                      <XLg />
                    </button>
                  </Form.Group>
                </div>
              ))}
              <Button
                variant="outline-secondary"
                onClick={() => pleadCollectionAdd(allegations, setAllegations)}
                className={`${styles.actionButton} ${styles.moreLeft}`}
              >
                + Add Another
              </Button>
            </>
          ) : (
            <>
              <p>No items available</p>
              <Button
                variant="primary"
                onClick={() => pleadCollectionAdd(allegations, setAllegations)}
              >
                Add
              </Button>
            </>
          )}
          <div className={styles.pendingSectionHeader}>
            <p>Request Types to Draft</p>
          </div>
          <div className={styles.pendingSectionFields}>
            <div className={styles.requestTypeFields}>
              <label htmlFor="set-number">Set Number</label>
              <Form.Control
                id="set-number"
                type="number"
                min="1"
                style={{ width: 80 }}
                className="mb-3"
                value={setNumber}
                size="sm"
                onChange={(event) =>
                  setSetNumber(Number.parseInt(event.currentTarget.value, 10))
                }
              />
              <hr />
              <Form.Group>
                {/* RFA Checkbox and Number */}
                <Form.Check
                  id="rfa-check"
                  type="checkbox"
                  label="Requests for Admission"
                  value={DocumentSubType.RequestsForAdmission}
                  onChange={(e) => handleRequestTypeChange(e)}
                  checked={selectedRequestTypes.some(
                    (item) =>
                      item.subtype === DocumentSubType.RequestsForAdmission,
                  )}
                />
                {setNumber > 1 &&
                  selectedRequestTypes.some(
                    (item) =>
                      item.subtype === DocumentSubType.RequestsForAdmission,
                  ) && (
                    <>
                      <label htmlFor="rfa-start-num">Start Number</label>
                      <Form.Control
                        id="rfa-start-num"
                        className="mb-3"
                        type="number"
                        min="1"
                        style={{ width: 80 }}
                        size="sm"
                        value={
                          selectedRequestTypes.find(
                            (item) =>
                              item.subtype ===
                              DocumentSubType.RequestsForAdmission,
                          )?.number || 1
                        }
                        onChange={(e) =>
                          handleRequestNumberChange(
                            e as ChangeEvent<HTMLInputElement>,
                            DocumentSubType.RequestsForAdmission,
                          )
                        }
                        disabled={
                          !selectedRequestTypes.some(
                            (item) =>
                              item.subtype ===
                              DocumentSubType.RequestsForAdmission,
                          )
                        }
                      />
                    </>
                  )}

                {/* Interrogatories Checkbox and Number */}
                <Form.Check
                  id="int-check"
                  type="checkbox"
                  label="Interrogatories"
                  value={DocumentSubType.SpecialInterrogatorries}
                  onChange={(e) => handleRequestTypeChange(e)}
                  checked={selectedRequestTypes.some(
                    (item) =>
                      item.subtype === DocumentSubType.SpecialInterrogatorries,
                  )}
                />
                {setNumber > 1 &&
                  selectedRequestTypes.some(
                    (item) =>
                      item.subtype === DocumentSubType.SpecialInterrogatorries,
                  ) && (
                    <>
                      <label htmlFor="int-start-num">Start Number</label>
                      <Form.Control
                        id="int-start-num"
                        className="mb-3"
                        type="number"
                        min="1"
                        style={{ width: 80 }}
                        size="sm"
                        value={
                          selectedRequestTypes.find(
                            (item) =>
                              item.subtype ===
                              DocumentSubType.SpecialInterrogatorries,
                          )?.number || 1
                        }
                        onChange={(e) =>
                          handleRequestNumberChange(
                            e as ChangeEvent<HTMLInputElement>,
                            DocumentSubType.SpecialInterrogatorries,
                          )
                        }
                        disabled={
                          !selectedRequestTypes.some(
                            (item) =>
                              item.subtype ===
                              DocumentSubType.SpecialInterrogatorries,
                          )
                        }
                      />
                    </>
                  )}

                <Form.Check
                  id="rfp-check"
                  type="checkbox"
                  label="Requests for Production"
                  value={DocumentSubType.RequestsForProduction}
                  onChange={(e) => handleRequestTypeChange(e)}
                  checked={selectedRequestTypes.some(
                    (item) =>
                      item.subtype === DocumentSubType.RequestsForProduction,
                  )}
                />
                {setNumber > 1 &&
                  selectedRequestTypes.some(
                    (item) =>
                      item.subtype === DocumentSubType.RequestsForProduction,
                  ) && (
                    <>
                      <label htmlFor="rfp-start-num">Start Number</label>
                      <Form.Control
                        id="rfp-start-num"
                        className="mb-3"
                        type="number"
                        min="1"
                        style={{ width: 80 }}
                        size="sm"
                        value={
                          selectedRequestTypes.find(
                            (item) =>
                              item.subtype ===
                              DocumentSubType.RequestsForProduction,
                          )?.number || 1
                        }
                        onChange={(e) =>
                          handleRequestNumberChange(
                            e as ChangeEvent<HTMLInputElement>,
                            DocumentSubType.RequestsForProduction,
                          )
                        }
                        disabled={
                          !selectedRequestTypes.some(
                            (item) =>
                              item.subtype ===
                              DocumentSubType.RequestsForProduction,
                          )
                        }
                      />
                    </>
                  )}
              </Form.Group>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReviewPageFourPd;
